import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { ImageLogoForm } from "../models/ImageLogoForm";
import { InputWithSuffix } from "./InputWithSuffix";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { TransferMethod } from "../../../../core/models/entities/LogoOptions";
import {
  correctLogoSizeForTransferMethod,
  fitSizeToTransferMethod,
} from "../../../../utils/functions/SizeUtil";
import { useConfirmationDialog } from "../../../components/Dialogs/confirmation-dialog";
import { enviroment } from "../../../../enviroment";

// Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED: a typed size above the
// method's max size is fitted EXACTLY as the API's policy fits it (whole mm
// in, one rounding half away from zero, orientation kept -
// fitSizeToTransferMethod), so the field never shows a size 1 mm off the
// one the server would store for it (7 x 431 under 300x400 is 6 x 400, not
// 7 x 400). The number is still the server's `size`. Flag off = today's
// two-decimal correction.
function fitTypedSizeToMethod(
  width: number,
  height: number,
  maxSize: string,
): { width: number; height: number } {
  if (!enviroment.logoMethodMaxSizeEnabled) {
    return correctLogoSizeForTransferMethod(width, height, maxSize);
  }
  return fitSizeToTransferMethod(
    Math.max(1, Math.round(width)),
    Math.max(1, Math.round(height)),
    maxSize,
  );
}

interface Props {
  width: number;
  height: number;
  onSizeChange: (width: number, height: number) => void;
  onResize: () => void;
  disabled?: boolean;
  transferMethod?: TransferMethod | null;
}

export function LogoResize({
  width,
  height,
  onSizeChange,
  onResize,
  disabled = false,
  transferMethod,
}: Props) {
  const { t } = useTranslation();
  const [inputWidth, setInputWidth] = useState(width.toString());
  const [inputHeight, setInputHeight] = useState(height.toString());

  const prevWidthRef = useRef(width);
  const prevHeightRef = useRef(height);

  // Stable aspect ratio (width/height) for proportional calculations.
  // Using current props each keystroke causes compounding rounding errors
  // that can drive one dimension to 0, making recovery impossible.
  const aspectRatio = useRef(height > 0 ? width / height : 1);

  // Tracks which field the user is actively editing so we can distinguish
  // internal prop changes (from typing) vs external ones (e.g. transfer method change).
  const editingFieldRef = useRef<"width" | "height" | null>(null);

  const confirmResizeDialog = useConfirmationDialog(
    t("logoEditorComponents.confirmResize", {
      width: inputWidth,
      height: inputHeight,
    }),
    () => {
      onResize();
    },
  );

  useEffect(() => {
    // Update aspect ratio only on external changes (not from user typing)
    if (editingFieldRef.current === null && width > 0 && height > 0) {
      aspectRatio.current = width / height;
    }

    if (prevWidthRef.current !== width) {
      setInputWidth(width.toString());
      prevWidthRef.current = width;
    }
    if (prevHeightRef.current !== height) {
      setInputHeight(height.toString());
      prevHeightRef.current = height;
    }

    editingFieldRef.current = null;
  }, [width, height]);

  function handleResizeClick() {
    const w = parseFloat(inputWidth);
    const h = parseFloat(inputHeight);

    if (w <= 30 && h <= 30) {
      confirmResizeDialog.open();
    } else {
      onResize();
    }
  }

  function handleWidthChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setInputWidth(value);

    if (
      value.trim().length === 0 ||
      parseFloat(value) === 0 ||
      isNaN(parseFloat(value))
    ) {
      return;
    }

    const newWidth = parseFloat(value);
    const newHeight = newWidth / aspectRatio.current;

    editingFieldRef.current = "width";

    // Apply size correction if transfer method is available
    if (transferMethod?.size) {
      const correctedSize = fitTypedSizeToMethod(
        newWidth,
        newHeight,
        transferMethod.size,
      );
      onSizeChange(
        Math.max(1, Math.round(correctedSize.width)),
        Math.max(1, Math.round(correctedSize.height)),
      );
    } else {
      onSizeChange(Math.round(newWidth), Math.max(1, Math.round(newHeight)));
    }
  }

  function handleHeightChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setInputHeight(value);

    if (
      value.trim().length === 0 ||
      parseFloat(value) === 0 ||
      isNaN(parseFloat(value))
    ) {
      return;
    }

    const newHeight = parseFloat(value);
    const newWidth = newHeight * aspectRatio.current;

    editingFieldRef.current = "height";

    // Apply size correction if transfer method is available
    if (transferMethod?.size) {
      const correctedSize = fitTypedSizeToMethod(
        newWidth,
        newHeight,
        transferMethod.size,
      );
      onSizeChange(
        Math.max(1, Math.round(correctedSize.width)),
        Math.max(1, Math.round(correctedSize.height)),
      );
    } else {
      onSizeChange(Math.max(1, Math.round(newWidth)), Math.round(newHeight));
    }
  }

  const isValid =
    !isNaN(parseFloat(inputWidth)) &&
    parseFloat(inputWidth) > 0 &&
    !isNaN(parseFloat(inputHeight)) &&
    parseFloat(inputHeight) > 0;

  return (
    <>
      {confirmResizeDialog.component}
      <div className={"flex items-center space-x-2"}>
        <label className={"uppercase"}>{t("logoEditorComponents.size")}</label>
        <InputWithSuffix
          disabled={disabled}
          className={"!w-20 h-9"}
          suffix={t("logoEditorComponents.mmUnit")}
          name={nameOf<ImageLogoForm>((lf) => lf.widthMm)}
          type="number"
          value={inputWidth}
          onChange={handleWidthChange}
        />
        <label>X</label>
        <InputWithSuffix
          disabled={disabled}
          className={"!w-20 h-9"}
          suffix={t("logoEditorComponents.mmUnit")}
          name={nameOf<ImageLogoForm>((lf) => lf.heightMm)}
          type="number"
          value={inputHeight}
          onChange={handleHeightChange}
        />
        <PrimaryButton
          width={"sm"}
          className={"px-4"}
          onClick={handleResizeClick}
          disabled={disabled || !isValid}
        >
          <FontAwesomeIcon icon={faArrowsRotate} />
          <span className={"flex-1"}>
            {t("logoEditorComponents.changeSize")}
          </span>
        </PrimaryButton>
      </div>
    </>
  );
}
